import * as XLSX from 'xlsx';
import {Injectable} from '@angular/core';
import {saveAs} from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class ExcelUtilService {
  constructor() {}

  public exportSimpleExcelWithBorders(fileName: string = 'simple_with_borders.xlsx'): void {
    const worksheet: XLSX.WorkSheet = {};
    const workbook = XLSX.utils.book_new();

    // Define cell with value and borders
    const cellWithBorder = {
      v: 'Test',
      s: {
        border: {
          top: {style: 'thin', color: {rgb: '000000'}},
          bottom: {style: 'thin', color: {rgb: '000000'}},
          left: {style: 'thin', color: {rgb: '000000'}},
          right: {style: 'thin', color: {rgb: '000000'}},
        },
      },
    };

    // Assign the cell to the worksheet
    worksheet['A1'] = cellWithBorder;
    worksheet['B2'] = cellWithBorder;
    worksheet['C3'] = cellWithBorder;

    // Define the worksheet range
    worksheet['!ref'] = 'A1:C3';

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Write the workbook to a file
    const excelBuffer: any = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    this.saveAsExcelFile(excelBuffer, fileName);
  }

  public exportHtmlToExcel(htmlContent: string, fileName: string = 'export.xlsx'): void {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const tables = tempDiv.querySelectorAll('table');

    let workbook = XLSX.utils.book_new();
    let worksheet: XLSX.WorkSheet | null = null;
    let startRow = 0;

    tables.forEach((table, index) => {
      const tempWorksheet = this.convertTableToSheetWithStyles(table);

      if (!worksheet) {
        worksheet = tempWorksheet;
      } else {
        startRow = XLSX.utils.decode_range(worksheet['!ref'] || '').e.r + 3; // +3 for two empty rows

        this.appendTableToSheet(worksheet, tempWorksheet, startRow);
      }

      const cols = this.autoFitColumns(worksheet);
      worksheet['!cols'] = cols;
    });

    if (worksheet) {
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      const excelBuffer: any = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
      this.saveAsExcelFile(excelBuffer, fileName);
    }
  }

  private convertTableToSheetWithStyles(table: HTMLElement): XLSX.WorkSheet {
    const worksheet: XLSX.WorkSheet = {};
    const rows = table.querySelectorAll('tr');

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('td, th');
      cells.forEach((cell, cellIndex) => {
        const cellAddress = XLSX.utils.encode_cell({r: rowIndex, c: cellIndex});
        const cellValue = cell.textContent || '';

        worksheet[cellAddress] = {v: cellValue};

        // Apply bold styling if the cell has the font-weight-bold class
        if (cell.classList.contains('font-weight-bold')) {
          worksheet[cellAddress].s = {
            font: {
              bold: true,
            },
          };
        }

        // Apply borders to all cells
        worksheet[cellAddress].s = worksheet[cellAddress].s || {};
        worksheet[cellAddress].s.border = {
          top: {style: 'thin', color: {rgb: '000000'}},
          bottom: {style: 'thin', color: {rgb: '000000'}},
          left: {style: 'thin', color: {rgb: '000000'}},
          right: {style: 'thin', color: {rgb: '000000'}},
        };
      });
    });

    worksheet['!ref'] = XLSX.utils.encode_range({
      s: {r: 0, c: 0},
      e: {r: rows.length - 1, c: rows[0].children.length - 1},
    });

    return worksheet;
  }

  private appendTableToSheet(destSheet: XLSX.WorkSheet, sourceSheet: XLSX.WorkSheet, startRow: number): void {
    const sourceRange = XLSX.utils.decode_range(sourceSheet['!ref'] || '');

    for (let R = sourceRange.s.r; R <= sourceRange.e.r; ++R) {
      for (let C = sourceRange.s.c; C <= sourceRange.e.c; ++C) {
        const sourceCell = XLSX.utils.encode_cell({r: R, c: C});
        const destCell = XLSX.utils.encode_cell({r: R + startRow, c: C});
        destSheet[destCell] = sourceSheet[sourceCell];

        // Copy formatting if present
        if (sourceSheet[sourceCell] && sourceSheet[sourceCell].s) {
          if (!destSheet[destCell]) destSheet[destCell] = {};
          destSheet[destCell].s = sourceSheet[sourceCell].s;
        }
      }
    }

    const destRange = XLSX.utils.decode_range(destSheet['!ref'] || '');
    destSheet['!ref'] = XLSX.utils.encode_range(destRange.s, {
      r: startRow + (sourceRange.e.r - sourceRange.s.r),
      c: Math.max(destRange.e.c, sourceRange.e.c),
    });
  }

  private getRowCount(sheet: XLSX.WorkSheet): number {
    const range = XLSX.utils.decode_range(sheet['!ref'] || '');
    return range.e.r - range.s.r + 1;
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {type: EXCEL_TYPE});
    saveAs(data, fileName);
  }

  private autoFitColumns(worksheet: XLSX.WorkSheet): {wpx: number}[] {
    const result = [];

    const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10; // Start with a minimum width

      for (let row = range.s.r; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({r: row, c: col});
        const cell = worksheet[cellAddress];

        if (cell && cell.v) {
          const cellValue = cell.v.toString();
          const width = cellValue.length * 7; // Estimate column width
          if (width > maxWidth) {
            maxWidth = width;
          }
        }
      }

      result[col] = {wpx: maxWidth}; // Set the column width in pixels
    }

    return result;
  }
}

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
